import { useCallback } from 'react';
import { isNil } from 'lodash';
import { useRecoilValue } from 'recoil';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import { Gen5AccessType } from '@terragotech/gen5-shared-utilities';
import {
  ADD_SAVED_VIEW_MUTATION,
  AddSavedViewArgs,
  AddSavedViewResponseData,
  SAVED_VIEW_QUERY,
  SAVED_VIEWS_QUERY,
  SavedViewResponse,
  SavedViewArgs,
  SavedViewResponseData,
  SavedViewsArgs,
  SavedViewsResponseData,
  DeleteSavedViewResponseData,
  DeleteSavedViewArgs,
  DELETE_SAVED_VIEW_MUTATION,
  SavedViewBaseResponse,
  SavedViewMetadataResponse,
  AddSavedViewResponse,
} from '../graphql/savedView';
import { configState } from '../recoil/atoms';
import { useViewstate } from './useViewstate';

export type SaveCurrentViewParams = Pick<AddSavedViewArgs['savedView'], 'name' | 'roleIds' | 'userIds'>;
export type SavedViewsResponseItem = Omit<SavedViewBaseResponse & SavedViewMetadataResponse, '__typename'>;

export type UseSavedViewsReturnType = {
  getSavedViewDetail: (variables: SavedViewArgs) => Promise<SavedViewResponse>;
  loadSavedView: (savedViewId: SavedViewResponse['id']) => Promise<void>;
  resetCurrentView: () => Promise<void>;
  saveCurrentView: (newView: SaveCurrentViewParams) => Promise<AddSavedViewResponse['id']>;
  removeSavedView: (savedViewId: SavedViewResponse['id']) => Promise<void>;
  refreshSavedViews: () => Promise<SavedViewsResponseItem[] | undefined>;
  savedViews?: SavedViewsResponseItem[];
};

export const useSavedViews = (): UseSavedViewsReturnType => {
  const { appInfo } = useRecoilValue(configState);
  const { getViewstate, getViewstateAsync, setViewstate, setViewstateAsync, resetViewstate, resetViewstateAsync } =
    useViewstate();

  const [getSavedView, _savedViewResult] = useLazyQuery<SavedViewResponseData, SavedViewArgs>(SAVED_VIEW_QUERY, {
    fetchPolicy: 'cache-first',
  });

  const { data: savedViewsResponseData, refetch: refetchSavedViews } = useQuery<SavedViewsResponseData, SavedViewsArgs>(
    SAVED_VIEWS_QUERY,
    {
      fetchPolicy: 'cache-and-network',
      variables: {
        filter: {
          app: Gen5AccessType.WEB,
        },
        includeMetadata: true,
        includeDetails: false,
        includeRelated: false,
      },
    }
  );

  const [addSavedView, _addSavedViewResponse] = useMutation<AddSavedViewResponseData, AddSavedViewArgs>(
    ADD_SAVED_VIEW_MUTATION,
    {
      fetchPolicy: 'no-cache',
    }
  );

  const [deleteSavedView, _deleteSavedViewResponse] = useMutation<DeleteSavedViewResponseData, DeleteSavedViewArgs>(
    DELETE_SAVED_VIEW_MUTATION,
    {
      fetchPolicy: 'no-cache',
    }
  );

  // TODO: Optimize to conditionally include `viewstate` data, based on variable or parameter.
  const getSavedViewDetail = useCallback<UseSavedViewsReturnType['getSavedViewDetail']>(async (variables) => {
    const { data, error } = await getSavedView({ variables });
    if (!isNil(error)) {
      throw `An error occurred while loading view details: ${error.message}`;
    } else if (isNil(data)) {
      throw `An anomalous error occurred while loading view details. Please try again, or contact the administrator if the problem persists.`;
    }
    return data.savedView;
  }, [getSavedView]);

  // TODO: Optimize query fields.
  const loadSavedView = useCallback<UseSavedViewsReturnType['loadSavedView']>(
    async savedViewId => {
      const { data, error } = await getSavedView({ variables: { id: savedViewId } });
      if (!isNil(error)) {
        throw `An error occurred while loading view data: ${error.message}`;
      } else if (isNil(data)) {
        throw `An anomalous error occurred while loading view data. Please try again, or contact the administrator if the problem persists.`;
      }
      await setViewstateAsync(data.savedView.viewstate); // setViewstate(data.savedView.viewstate);
    },
    [getSavedView, setViewstate, setViewstateAsync]
  );

  const resetCurrentView = useCallback<UseSavedViewsReturnType['resetCurrentView']>(async () => {
    await resetViewstateAsync({
      Columns: {},
      // Language: null,
      MenuSelections: {
        mapLabelVisibility: false,
        cardViewVisibility: true,
        pinClusteringEnabled: true,
      },
      Sort: {},
    }); // resetViewstate();
  }, [resetViewstate, resetViewstateAsync]);

  const saveCurrentView = useCallback<UseSavedViewsReturnType['saveCurrentView']>(
    async newView => {
      const viewstate = await getViewstateAsync(); // getViewstate();
      const { data, errors } = await addSavedView({
        variables: {
          savedView: {
            ...newView,
            app: Gen5AccessType.WEB,
            appVersion: appInfo?.version,
            viewstate,
          },
        },
      });
      if (errors?.length) {
        throw `An error occurred while saving view data: ${errors[0].message}`;
      } else if (isNil(data)) {
        throw `An anomalous error occurred while saving view data. Please try again, or contact the administrator if the problem persists.`;
      }
      await refetchSavedViews();
      return data.addSavedView.id;
    },
    [getViewstate, getViewstateAsync, addSavedView, appInfo, refetchSavedViews]
  );

  const removeSavedView = useCallback<UseSavedViewsReturnType['removeSavedView']>(
    async savedViewId => {
      const { data, errors } = await deleteSavedView({ variables: { id: savedViewId } });
      if (errors?.length) {
        throw `An error occurred while deleting view data: ${errors[0].message}`;
      } else if (data?.deleteSavedView.status !== 'success') {
        throw `An anomalous error occurred while deleting view data. Please try again, or contact the administrator if the problem persists.`;
      }
      await refetchSavedViews();
    },
    [deleteSavedView, refetchSavedViews]
  );

  const refreshSavedViews = useCallback(async () => {
    const { data, error } = await refetchSavedViews();
    if (!isNil(error)) {
      throw `An error occurred while refreshing view data: ${error.message}`;
    } else if (isNil(data)) {
      throw `An anomalous error occurred while refreshing view data. Please try again, or contact the administrator if the problem persists.`;
    }
    return data.savedViews;
  }, [refetchSavedViews]);

  return {
    getSavedViewDetail,
    loadSavedView,
    resetCurrentView,
    saveCurrentView,
    removeSavedView,
    refreshSavedViews,
    savedViews: savedViewsResponseData?.savedViews,
  };
};

export default useSavedViews;
